<script setup lang="ts">
import { useDialog } from 'primevue/usedialog'
import { getErrorImage, PICKUP_ERRORS, StoreErrorType } from '~/lib/models/errors'
import { Coords } from '~/lib/models/geolocation'
import { GTMContentGroup } from '~/lib/models/gtm'
import { CounterType } from '~/lib/services/store/order/order.dto'
import { isPreviousToPayment } from '~/utils/orderUtils'

const props = defineProps<{
  mobile: boolean
  userLocation?: Coords
}>()

const emit = defineEmits(['set-step', 'set-area', 'close', 'set-counter-list'])
const appStore = useAppStore()
const authStore = useAuthStore()
const dialog = useDialog()
const { handleStoreError } = useStoreResponse()
const { cancelOrder } = useOrderActions()
const { t } = useLocale()
const { myOrdersRoute, orderDetailRoute, navigateToHome } = useMcdRoutes()
const { trackPageView, trackSelectPickup } = useGTM()
const { orderServiceRef } = useStoreService()
const orderService = orderServiceRef()

const loading = ref(true)
const permittedAreas = ref<{ name: string; disabled: boolean }[]>()
const dualPoint = ref<boolean>()
const saleTypeEnabled = ref<boolean>(false)
const counters = ref<CounterType[]>([])

const howToArriveUrl = computed(() =>
  getGoogleMapsUrl(getRestaurantCoordinates(), props.userLocation)
)

const gtmPickupInfo = computed(() => {
  if (!appStore.currentOrder)
    return {
      restaurantCode: '',
      isPreOrder: false,
    }
  const order = appStore.currentOrder
  return {
    restaurantCode: order.restaurant.code,
    isPreOrder: order.orderMcDia,
  }
})

const getRestaurantCoordinates = () => {
  if (!appStore.currentOrder) return { lat: 0, lng: 0 }
  const { latitude: lat, longitude: lng } = appStore.currentOrder.deliveryPlace
  return { lat, lng }
}

const setArea = (area: string) => {
  // track select_pickup
  trackSelectPickup({ area, ...gtmPickupInfo.value })
  const event = {
    area,
    dualPoint: dualPoint.value,
    saleTypeEnabled: saleTypeEnabled.value,
    counters: counters.value,
  }
  emit('set-area', event)
}

const close = () => {
  emit('close')
}

const handlePermittedAreasErrors = async (error: any) => {
  const { key } = handleStoreError(error)
  const hasCancelButton = (
    [StoreErrorType.status651, StoreErrorType.status611] as string[]
  ).includes(key)

  let template = ''
  let cssClass = ''
  if (PICKUP_ERRORS.includes(key)) {
    template = `pickup_${key}`
    cssClass = 'pickup-error'
  } else template = key

  const result = await showDialogAlert({
    dialog,
    data: {
      title: t(`errors.alerts.${template}.title`),
      body: t(`errors.alerts.${template}.body`),
      btnPrimary: t(`errors.alerts.${template}.accept`),
      btnSecondary: hasCancelButton ? t(`${template}.cancel`) : undefined,
      headerImage: getErrorImage(template),
    },
    customCssClass: cssClass ?? undefined,
  })

  switch (key) {
    case StoreErrorType.status651:
      close()
      return await navigateTo(myOrdersRoute())
    case StoreErrorType.status611:
      if (result) return await navigateToHome()
      break
    case StoreErrorType.status618:
      close()
      break
    case StoreErrorType.status604:
    case StoreErrorType.status633:
    case StoreErrorType.status653:
    case StoreErrorType.status673: {
      const orderId = appStore.currentOrder?.id || ''
      close()
      if (result) return await navigateTo(orderDetailRoute(orderId))
    }
  }

  const tokenExpired = key === StoreErrorType.status401
  const isDayPartError = key === StoreErrorType.status634

  if (!tokenExpired && isDayPartError) {
    if (result)
      await cancelOrder({
        id: appStore.currentOrder!.id,
        isPaymentPending: isPreviousToPayment(appStore.currentOrder!),
      })
  }
  close()
}

onMounted(async () => {
  try {
    // reset area
    const event = { area: undefined, dualPoint: false, saleTypeEnabled: false, counters: [] }
    emit('set-area', event)
    trackPageView(
      {
        cg: GTMContentGroup.primary.ECOMMERCE,
        cg2: GTMContentGroup.secondary.ORDER,
        cg3: GTMContentGroup.tertiary.PICKUP_METHODS,
      },
      true
    )
    const customerToken = authStore.token!
    const countryCode = appStore.countryCode
    const {
      permittedAreas: areas,
      dualPoint: dualPointEnabled,
      saleTypeActive,
      counters: counterList,
    } = await orderService.getPickupPermittedAreas({
      customerToken,
      countryCode,
      orderId: appStore.currentOrder!.id,
    })
    permittedAreas.value = areas
    dualPoint.value = dualPointEnabled
    saleTypeEnabled.value = saleTypeActive
    counters.value = counterList ?? []
    emit('set-counter-list', counterList)
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log('🚀 ~ file: PickupAreaSelectionModal.vue:22 ~ onMounted ~ error:', error)
    await handlePermittedAreasErrors(error)
  } finally {
    loading.value = false
  }
})
</script>

<template>
  <main data-test="pickup-area-selection">
    <ModalCenter :content-full="mobile" :add-close-button="false">
      <template #header>
        <div class="relative flex w-full flex-row items-center gap-4">
          <template v-if="mobile">
            <img
              v-if="mobile"
              class="h-8 cursor-pointer md:h-6"
              src="/images/icons/arrowLeft.svg"
              alt="close"
              @click="close"
            />
            <h2 class="text-lg font-bold">{{ t('pickupMethods.areaSelection.backTitle') }}</h2>
          </template>
          <CloseIcon v-else width="16" height="16" @click="close" />
        </div>
      </template>
      <template #content>
        <div v-if="loading" class="flex flex-1 flex-col items-center justify-center">
          <Loader fit />
        </div>
        <section v-else class="mx-auto w-full p-4 md:w-[85%] md:p-0">
          <template v-if="mobile">
            <!-- Restaurant heading -->
            <PickupRestaurantHeader
              :restaurant="appStore.currentOrder?.restaurant"
              :coords="getRestaurantCoordinates()"
            />

            <!-- How to arrive -->
            <div class="my-4 flex">
              <a
                :href="howToArriveUrl"
                target="_blank"
                class="ml-auto flex flex-row items-center gap-2 text-[10px] text-xs/4 text-mcd-secondaryBlue"
              >
                <span class="block">
                  {{ t('restaurant.detail.howToArrive') }}
                </span>
                <Chevron fill="#006BAE" class="h-4 w-4" />
              </a>
            </div>

            <!-- Alert starting order disclaimer -->
            <div class="my-4 flex flex-row items-center gap-3 bg-mcd-neutralGrey p-4">
              <img class="h-8 w-8" src="/images/icons/warning-alert.svg" alt="warning" />
              <p class="text-xs">{{ t('pickupMethods.areaSelection.info') }}</p>
            </div>
          </template>

          <LazyPermittedAreas
            v-if="permittedAreas"
            :areas="permittedAreas"
            @select-area="setArea"
          />

          <!-- Help links - FAQs, whatsapp link -->
          <HelpLinks class="py-5" />
        </section>
      </template>
    </ModalCenter>
  </main>
</template>
